import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Lock, Zap, ShieldCheck, CreditCard, ArrowRight } from 'lucide-react';
import { Countdown, ScrollButton } from './Shared';
type Pack = {
  id: string;
  emoji: string;
  name: string;
  detail: string;
  oldPrice: string;
  price: string;
  bestseller?: boolean;
  savings?: string;
};
const packs: Pack[] = [{
  id: 'mat',
  emoji: '📘',
  name: 'PACK PILATES MAT',
  detail: '6 Tomos de Mat + 6 Bonus',
  oldPrice: '$26.990',
  price: '$16.990'
}, {
  id: 'reformer',
  emoji: '📕',
  name: 'PACK PILATES REFORMER',
  detail: '6 Tomos de Reformer + 6 Bonus',
  oldPrice: '$28.990',
  price: '$18.990'
}, {
  id: 'elite',
  emoji: '🏆',
  name: 'MEGAPACK ÉLITE',
  detail: '12 Tomos + 6 Bonus + 2 EBOOKS ÉLITE',
  oldPrice: '$49.990',
  price: '$27.990',
  bestseller: true,
  savings: 'AHORRÁS $22.000'
}];
export const Pricing = () => {
  const [selected, setSelected] = useState<string>('elite');
  const selectedPack = packs.find(p => p.id === selected)!;
  const ctaLabel = selected === 'elite' ? 'SÍ, QUIERO EL MEGAPACK ÉLITE' : selected === 'reformer' ? 'SÍ, QUIERO EL PACK REFORMER' : 'SÍ, QUIERO EL PACK MAT';
  return <section id="precio" className="py-20 bg-brand-charcoal scroll-mt-10" data-id="element-84">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-85">
        {/* Header & Countdown */}
        <div className="text-center mb-12" data-id="element-86">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-87">
            ⏰ PRECIO DE LANZAMIENTO
          </h2>
          <p className="text-lg text-brand-terracotta font-bold mb-8 uppercase tracking-wider" data-id="element-88">
            Oferta por tiempo limitado
          </p>
          <div className="mb-6" data-id="element-89">
            <Countdown data-id="element-90" />
          </div>
          <p className="text-red-300 font-medium text-sm" data-id="element-91">
            🔴 El precio sube cuando termina el contador
          </p>
        </div>

        {/* Pricing Card */}
        <div className="bg-white rounded-[28px] p-6 sm:p-10 shadow-2xl" data-id="element-92">
          <div className="text-center mb-8" data-id="element-93">
            <h3 className="text-3xl sm:text-4xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-94">
              Elegí tu Pack:
            </h3>
            <p className="text-gray-500 text-base sm:text-lg max-w-md mx-auto" data-id="element-95">
              Todas las opciones incluyen los 6 Bonus estratégicos
            </p>
          </div>

          {/* Pack Options */}
          <div className="space-y-4 mb-8" data-id="element-96">
            {packs.map(pack => {
            const isSelected = selected === pack.id;
            return <motion.button key={pack.id} type="button" onClick={() => setSelected(pack.id)} whileTap={{
              scale: 0.99
            }} className={`w-full text-left rounded-2xl border-2 transition-all p-5 sm:p-6 ${isSelected ? 'border-brand-terracotta bg-brand-cream' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-97">
                  <div className="flex items-center gap-4" data-id="element-98">
                    {/* Radio */}
                    <div className="flex-shrink-0" data-id="element-99">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-brand-terracotta' : 'border-gray-300'}`} data-id="element-100">
                        {isSelected && <div className="w-3 h-3 rounded-full bg-brand-terracotta" data-id="element-101" />}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex items-center gap-3 sm:gap-4 min-w-0" data-id="element-102">
                      <div className="text-3xl sm:text-4xl flex-shrink-0" data-id="element-103">
                        {pack.emoji}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-104">
                        <div className="font-bold text-brand-charcoal text-sm sm:text-base leading-tight mb-1" data-id="element-105">
                          {pack.name}
                        </div>
                        {pack.bestseller && <div className="flex flex-wrap gap-1.5 my-2" data-id="element-106">
                            <span className="inline-flex items-center gap-1 bg-brand-gold text-brand-charcoal text-[10px] sm:text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-107">
                              ⭐ MÁS ELEGIDO
                            </span>
                            {pack.savings && <span className="inline-block bg-brand-terracotta/10 text-brand-terracotta text-[10px] sm:text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-108">
                                {pack.savings}
                              </span>}
                          </div>}
                        <div className="text-xs sm:text-sm text-gray-500 leading-snug" data-id="element-109">
                          {pack.detail}
                        </div>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-right flex-shrink-0" data-id="element-110">
                      <div className="text-xs sm:text-sm text-gray-400 line-through leading-none mb-1" data-id="element-111">
                        {pack.oldPrice}
                      </div>
                      <div className={`text-xl sm:text-3xl font-bold leading-none ${isSelected ? 'text-brand-terracotta' : 'text-brand-charcoal'}`} data-id="element-112">
                        {pack.price}
                      </div>
                    </div>
                  </div>
                </motion.button>;
          })}
          </div>

          {/* CTA Button */}
          <button type="button" className="w-full bg-brand-terracotta hover:bg-brand-terracotta-dark text-white text-base sm:text-lg font-bold py-5 px-6 rounded-2xl shadow-lg shadow-brand-terracotta/30 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-3" data-id="element-113">
            {ctaLabel}
            <ArrowRight size={20} data-id="element-114" />
          </button>

          <p className="text-center italic text-gray-500 text-sm mt-6 max-w-md mx-auto" data-id="element-115">
            {selected === 'elite' ? 'La opción más elegida por profesores de Pilates argentinos' : `Estás eligiendo el ${selectedPack.name.toLowerCase()} por ${selectedPack.price}`}
          </p>
        </div>

        {/* Trust Footer */}
        <div className="grid grid-cols-2 gap-y-4 gap-x-6 mt-10 max-w-md mx-auto text-white/80 text-sm" data-id="element-116">
          <span className="flex items-center gap-2" data-id="element-117">
            <Lock size={16} className="text-white/60" data-id="element-118" /> Pago seguro
          </span>
          <span className="flex items-center gap-2" data-id="element-119">
            <Zap size={16} className="text-brand-gold" data-id="element-120" /> Acceso en 5 min
          </span>
          <span className="flex items-center gap-2" data-id="element-121">
            <ShieldCheck size={16} className="text-green-400" data-id="element-122" /> Garantía 7 días
          </span>
          <span className="flex items-center gap-2" data-id="element-123">
            <CreditCard size={16} className="text-white/60" data-id="element-124" /> Tarjeta ·
            Transferencia
          </span>
        </div>
      </div>

      {/* Order Bump + Comparison on lighter background */}
      <div className="bg-brand-cream mt-20 pt-20 pb-4" data-id="element-125">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-126">
          {/* Order Bump */}
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-brand-sand mb-24 shadow-sm" data-id="element-127">
            <h3 className="text-3xl font-serif font-bold text-brand-charcoal mb-4 text-center" data-id="element-128">
              ¿Qué agrega el Megapack Élite?
            </h3>
            <p className="text-center text-gray-600 mb-10" data-id="element-129">
              Por solo unos pesos más, resolvés el año completo.
            </p>

            <div className="space-y-8 mb-10" data-id="element-130">
              <div className="flex gap-6" data-id="element-131">
                <div className="text-4xl" data-id="element-132">📅</div>
                <div data-id="element-133">
                  <h4 className="text-xl font-bold text-brand-charcoal mb-2" data-id="element-134">
                    Élite 1 — El Calendario de las 1.000 Sesiones
                  </h4>
                  <p className="text-gray-600 leading-relaxed" data-id="element-135">
                    La hoja de ruta de enero a diciembre. Plan de 52 semanas
                    semana a semana con exactamente qué Tomo y qué sesión dar
                    cada lunes del año. Nunca más te preguntés qué das mañana.
                  </p>
                </div>
              </div>
              <div className="h-px bg-brand-sand w-full" data-id="element-136"></div>
              <div className="flex gap-6" data-id="element-137">
                <div className="text-4xl" data-id="element-138">🔀</div>
                <div data-id="element-139">
                  <h4 className="text-xl font-bold text-brand-charcoal mb-2" data-id="element-140">
                    Élite 2 — Diccionario de Variaciones Infinitas
                  </h4>
                  <p className="text-gray-600 leading-relaxed" data-id="element-141">
                    Cómo transformar 1 ejercicio en 10 versiones distintas
                    usando solo 7 variables. Tus alumnos van a creer que
                    aprendés ejercicios nuevos cada semana. 100 variaciones
                    graficadas listas para usar.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-brand-cream p-6 rounded-xl border border-brand-sand text-center italic text-lg text-brand-charcoal font-serif" data-id="element-142">
              "Por unos pesos más te llevás el año entero resuelto y clases que
              nunca se repiten. La pregunta no es si vale. Es por qué no lo
              elegiste primero."
            </div>
          </div>

          {/* Comparison Table */}
          <div data-id="element-143">
            <h3 className="text-3xl font-serif font-bold text-brand-charcoal mb-10 text-center" data-id="element-144">
              ¿Cuánto cuesta NO tenerlo?
            </h3>
            <div className="bg-white rounded-2xl shadow-lg border border-brand-sand overflow-hidden" data-id="element-145">
              <div className="grid grid-cols-2 bg-brand-charcoal text-white p-6" data-id="element-146">
                <div className="font-bold text-lg text-center" data-id="element-147">
                  Sin PILATES 360
                </div>
                <div className="font-bold text-lg text-center text-brand-gold" data-id="element-148">
                  Con PILATES 360
                </div>
              </div>
              <div className="divide-y divide-brand-sand" data-id="element-149">
                {[['Horas planificando desde cero cada semana', '5 minutos. Todo listo.'], ['Sesiones improvisadas sin progresión real', '+150 sesiones graficadas por nivel'], ['Grupos sin sistema claro', 'Estructura clara para cada nivel'], ['Siempre los mismos ejercicios', '100 variaciones listas para usar'], ['Miedo a que un alumno se lesione', 'Checklist de seguridad incluido'], ['Terminar cada clase agotada mentalmente', 'Llegás con todo resuelto'], ['Sin poder justificar un aumento de precio', 'Sistema documentado y profesional']].map((row, i) => <div key={i} className="grid grid-cols-2 p-6 hover:bg-brand-cream transition-colors" data-id="element-150">
                    <div className="text-gray-600 pr-4" data-id="element-151">{row[0]}</div>
                    <div className="text-brand-charcoal font-semibold pl-4 border-l border-brand-sand flex items-center gap-3" data-id="element-152">
                      <Check className="text-green-500 flex-shrink-0" size={20} data-id="element-153" />{' '}
                      {row[1]}
                    </div>
                  </div>)}
              </div>
            </div>
            <p className="text-center text-xl font-serif font-bold text-brand-charcoal mt-8" data-id="element-154">
              El lunes podés improvisar o entrar con todo resuelto. Vos decidís.
            </p>
          </div>
        </div>
      </div>
    </section>;
};